import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { IconChevron, IconBinoculars, IconSparkles, IconClose } from '../components/icons.jsx';
import { useCurrentUser } from './CurrentUserContext.jsx';
import { useOptionalSiteAccess } from './SiteAccessContext.jsx';
import { rbac } from '@dha-nexus/shared';
import { isEssentialRoute } from '@dha-nexus/shared/navEssentials.js';
import { useOnboardingState, setNavView } from '../lib/onboardingState.js';
import { formatBuildDateTime, formatBuildVersion, getBuildInfo } from '../lib/buildInfo.js';
import { isRootAdminIdentity } from './rootAdminIdentity.js';
import { PrimeBrandMark } from './PrimeBrandMark.jsx';
import {
  NAV_WORKSPACES,
  resolveWorkspaces,
  SITE_ACCESS_ITEM,
  PM_GUIDE_RESULTS_ITEM,
} from './navRegistry.js';
import { useApi } from '../lib/useApi.js';
import { api } from '../lib/apiClient.js';
import {
  decodeNavPreferences,
  resolveRailModel,
  toggleSectionCollapsed,
  toggleSubmenuCollapsed,
  encodeNavPreferences,
  withBumpedRevision,
} from '../lib/navPreferences.js';
import './sideNavEssentials.css';

// Debounce window for the silent rail-collapse persistence (decision 6).
const COLLAPSE_PERSIST_DELAY_MS = 400;

// Exported for the SideNav persona-matrix test (AC-SHELL-1 / AC-UX-6). The
// canonical workspace catalog now lives in navRegistry.js so the rail and the
// personal-menu editor share one source of truth (PRIME-NAV-UX-001, decision 4).
export const SIDENAV_WORKSPACES = NAV_WORKSPACES;

// Deep-link target for the personal-menu editor hosted on the Settings page.
export const CUSTOMIZE_MENU_TARGET = '/settings#personal-navigation';

// Fires after the editor saves so the live rail refreshes its persisted layout
// without a full page reload (same-session showcase continuity).
export const NAV_PREFERENCES_CHANGED_EVENT = 'prime:nav-preferences-changed';

export function SideNav({
  expanded,
  onToggle,
  // PRIME-MOB-UX-001 (locked decision 2): the SAME rail, presented as an
  // off-canvas drawer at mobile widths. These props are inert on desktop —
  // the rail renders and behaves exactly as before when mobileDrawer is false.
  mobileDrawer = false,
  drawerOpen = false,
  onRequestClose,
}) {
  const { roles, user } = useCurrentUser();
  const userId = user?.id;
  // Drawer chrome refs (mobile only): the nav element hosts the focus trap; the
  // close button receives focus when the drawer opens.
  const navRef = useRef(null);
  const closeButtonRef = useRef(null);

  // On open, move focus into the drawer (its close affordance) so keyboard and
  // screen-reader users land inside the modal surface (locked decision 2).
  // Deferred to the next frame: the rail is visibility:hidden while closed, and
  // an element is not focusable until the browser has applied the drawer-open
  // class (which flips it visible) and recomputed style — calling focus()
  // synchronously in the effect races that and silently no-ops in real browsers.
  useEffect(() => {
    if (!(mobileDrawer && drawerOpen)) return undefined;
    let inner = 0;
    // Double rAF: the first frame lets the browser apply the drawer-open class
    // and flip the rail visible; focus on the second frame, once it is genuinely
    // focusable.
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => closeButtonRef.current?.focus());
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [mobileDrawer, drawerOpen]);

  // Focus trap + Escape while the drawer is open. Escape delegates to
  // onRequestClose (the shell returns focus to the hamburger); Tab wraps within
  // the drawer's focusable elements so focus can never escape to the page
  // behind the scrim.
  const handleDrawerKeyDown = useCallback(
    (event) => {
      if (!mobileDrawer || !drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestClose?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = navRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [mobileDrawer, drawerOpen, onRequestClose],
  );
  const buildInfo = getBuildInfo();
  const buildVersionLabel = formatBuildVersion(buildInfo.version);
  const buildTimeLabel = formatBuildDateTime(buildInfo.builtAt);
  const siteAccessState = useOptionalSiteAccess()?.state || null;
  const canSeeSiteAccess = Boolean(
    siteAccessState?.adminConsoleEnabled === true &&
      (siteAccessState?.isApprover || siteAccessState?.isOwner),
  );
  const isRootAdmin = isRootAdminIdentity(siteAccessState);

  // Server-persisted personal navigation (PRIME-NAV-UX-001). Fetched here so the
  // rail can render a user's saved custom layout. A missing/absent/decode-null
  // layout leaves today's essentials-first default rail untouched, which keeps
  // the extraction behavior-preserving for the existing SideNav tests.
  const prefsApi = useApi('/me/preferences');
  const refreshPreferences = prefsApi.refresh;
  const persistTimer = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onChanged = () => {
      clearTimeout(persistTimer.current);
      persistTimer.current = null;
      refreshPreferences();
    };
    window.addEventListener(NAV_PREFERENCES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(NAV_PREFERENCES_CHANGED_EVENT, onChanged);
  }, [refreshPreferences]);

  // Server-truth schema decoded from the fetched preferences.
  const storedSchema = useMemo(() => {
    const prefs =
      prefsApi.data?.data && typeof prefsApi.data.data === 'object' ? prefsApi.data.data : null;
    return prefs ? decodeNavPreferences(prefs) : null;
  }, [prefsApi.data]);

  // Local display schema. It starts as the server truth and absorbs optimistic
  // collapse toggles from the rail. When the server truth changes (a fresh load,
  // or the editor's own save broadcasting a refresh) we re-adopt it, dropping any
  // local collapse edits in favor of the authoritative layout. Content signature
  // (not object identity) drives the re-adopt so an unstable useApi reference
  // never wipes a user's in-progress collapse. (PRIME-NAV-UX-002, decision 6.)
  const [localSchema, setLocalSchema] = useState(storedSchema);
  const [adoptedSig, setAdoptedSig] = useState(() =>
    storedSchema ? JSON.stringify(storedSchema) : '',
  );
  const localRef = useRef(storedSchema);
  const revisionRef = useRef(storedSchema ? storedSchema.revision : null);
  const previousUserIdRef = useRef(userId);
  const identityMatchesLocalSchema = previousUserIdRef.current === userId;

  const storedSig = storedSchema ? JSON.stringify(storedSchema) : '';
  if (storedSig !== adoptedSig) {
    // Server truth wins, including over a collapse made before an unrelated
    // refresh; cancel the captured stale write before adopting that truth.
    clearTimeout(persistTimer.current);
    persistTimer.current = null;
    setAdoptedSig(storedSig);
    setLocalSchema(storedSchema);
    localRef.current = storedSchema;
    revisionRef.current = storedSchema ? storedSchema.revision : null;
  }
  useEffect(() => {
    localRef.current = localSchema;
  }, [localSchema]);
  useEffect(() => {
    if (previousUserIdRef.current === userId) return undefined;
    previousUserIdRef.current = userId;
    clearTimeout(persistTimer.current);
    persistTimer.current = null;
    localRef.current = null;
    revisionRef.current = null;
    setLocalSchema(null);

    let active = true;
    const refreshResult = refreshPreferences();
    if (refreshResult && typeof refreshResult.finally === 'function') {
      refreshResult.finally(() => {
        if (active) setAdoptedSig(`identity-refresh:${userId || ''}`);
      });
    }
    return () => {
      active = false;
    };
  }, [userId, refreshPreferences]);
  // Flush any pending debounced write when the rail unmounts.
  useEffect(() => () => clearTimeout(persistTimer.current), []);

  // Persist a collapse edit silently: debounced PATCH with a bumped revision, no
  // dirty bar or toast. Conflicts refresh server truth; other failures keep the
  // optimistic state so the next collapse change can retry (decision 6).
  const scheduleCollapsePersist = useCallback((nextSchema) => {
    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      persistTimer.current = null;
      const base = Number.isInteger(revisionRef.current) ? revisionRef.current : 0;
      const toSave = withBumpedRevision({ ...nextSchema, revision: base });
      Promise.resolve(api.patch('/me/preferences', encodeNavPreferences(toSave)))
        .then(() => {
          revisionRef.current = toSave.revision;
        })
        .catch((error) => {
          if (error?.code === 'REVISION_CONFLICT') refreshPreferences();
        });
    }, COLLAPSE_PERSIST_DELAY_MS);
  }, [refreshPreferences]);

  const onToggleSectionCollapse = useCallback(
    (sectionId) => {
      const next = toggleSectionCollapsed(localRef.current, sectionId);
      if (next === localRef.current) return;
      localRef.current = next;
      setLocalSchema(next);
      scheduleCollapsePersist(next);
    },
    [scheduleCollapsePersist],
  );

  const onToggleSubmenuCollapse = useCallback(
    (sectionId, submenuId) => {
      const next = toggleSubmenuCollapsed(localRef.current, sectionId, submenuId);
      if (next === localRef.current) return;
      localRef.current = next;
      setLocalSchema(next);
      scheduleCollapsePersist(next);
    },
    [scheduleCollapsePersist],
  );

  const customModel = useMemo(() => {
    if (!localSchema || !identityMatchesLocalSchema) return null;
    const model = resolveRailModel({ schema: localSchema, roles });
    return model && model.length ? model : null;
  }, [identityMatchesLocalSchema, localSchema, roles]);
  const hasCustomLayout = Boolean(customModel);

  // Essentials-first onboarding view (WP2) — the default-rail personalization
  // mechanism for a user who has NOT saved a custom layout yet (decision 5).
  const { navView } = useOnboardingState(userId);
  const renderedWorkspaces = resolveWorkspaces({ isRootAdmin, canSeeSiteAccess });

  // Honest full-set size for the "Show all features (N)" toggle label.
  const fullVisibleCount = renderedWorkspaces.reduce(
    (sum, s) => sum + s.items.filter((i) => rbac.hasAnyRole(roles, i.allow || [])).length,
    0,
  );
  const toggleLabel =
    navView === 'all' ? 'Show essentials' : `Show all features (${fullVisibleCount})`;

  // Session-injected specials that live outside the editable catalog (CAC
  // Approval, PM guide results). In custom mode they are pinned below the saved
  // sections so a customizing owner/admin never loses the console (the editor
  // does not expose them in 001 — see WORKER_RESULT.md).
  const pinnedSpecials = useMemo(() => {
    if (!hasCustomLayout) return [];
    const specials = [];
    if (isRootAdmin) specials.push(PM_GUIDE_RESULTS_ITEM);
    if (canSeeSiteAccess) specials.push(SITE_ACCESS_ITEM);
    const savedRoutes = new Set(
      customModel.flatMap((s) =>
        s.items.flatMap((i) => (i.type === 'submenu' ? i.items.map((p) => p.route) : [i.route])),
      ),
    );
    return specials.filter((item) => !savedRoutes.has(item.to));
  }, [hasCustomLayout, isRootAdmin, canSeeSiteAccess, customModel]);

  const [tip, setTip] = useState(null);
  function showTip(event, label) {
    const el = event.currentTarget;
    const labelEl = el.querySelector('.rail-item-label');
    const labelHidden = !labelEl || getComputedStyle(labelEl).display === 'none';
    if (!labelHidden) {
      setTip(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setTip({ label, top: Math.round(r.top + r.height / 2), left: Math.round(r.right + 12) });
  }
  const hideTip = () => setTip(null);

  const renderItem = (to, label, Icon) => (
    <NavLink
      key={to}
      to={to}
      className={({ isActive }) => `rail-item${isActive ? ' active' : ''}`}
      data-tour={`nav:${to}`}
      aria-label={label}
      onMouseEnter={(e) => showTip(e, label)}
      onMouseLeave={hideTip}
      onFocus={(e) => showTip(e, label)}
      onBlur={hideTip}
    >
      <Icon size={20} />
      <span className="rail-item-label">{label}</span>
    </NavLink>
  );

  // Collapsible submenu inside a custom section (decision 9). The header is a
  // native <button> so keyboard toggling comes for free; aria-expanded reflects
  // state and the item container stays mounted (CSS hides it) to avoid unmount
  // flicker. Collapse persists silently through onToggleSubmenuCollapse.
  const renderSubmenu = (sectionId, submenu) => (
    <div className="rail-submenu" key={`sub:${submenu.id}`}>
      <button
        type="button"
        className={`rail-submenu-toggle${submenu.collapsed ? ' is-collapsed' : ''}`}
        aria-expanded={!submenu.collapsed}
        onClick={() => onToggleSubmenuCollapse(sectionId, submenu.id)}
        onMouseEnter={(e) => showTip(e, submenu.label)}
        onMouseLeave={hideTip}
        onFocus={(e) => showTip(e, submenu.label)}
        onBlur={hideTip}
      >
        <IconChevron size={14} className="rail-collapse-chevron" />
        {submenu.emoji ? (
          <span className="rail-group-emoji" aria-hidden>
            {submenu.emoji}
          </span>
        ) : null}
        <span className="rail-item-label">{submenu.label}</span>
      </button>
      <div
        className={`rail-submenu-items${submenu.collapsed ? ' is-collapsed' : ''}`}
        hidden={submenu.collapsed}
      >
        {submenu.items.map((item) => renderItem(item.route, item.label, item.icon))}
      </div>
    </div>
  );

  // A saved custom section. Non-Home sections render a collapse toggle header
  // (chevron + emoji + label); Home stays a fixed, non-collapsible label (AC-01).
  const renderCustomSection = (section) => {
    const isHome = section.source === 'Home';
    const body = section.items.map((item) =>
      item.type === 'submenu'
        ? renderSubmenu(section.id, item)
        : renderItem(item.route, item.label, item.icon),
    );
    if (isHome) {
      return (
        <div className="rail-section" key={`custom:${section.id}`}>
          <div className="rail-section-label">{section.label}</div>
          {body}
        </div>
      );
    }
    return (
      <div
        className={`rail-section rail-section-custom${section.collapsed ? ' is-collapsed' : ''}`}
        key={`custom:${section.id}`}
      >
        <button
          type="button"
          className={`rail-section-toggle${section.collapsed ? ' is-collapsed' : ''}`}
          aria-expanded={!section.collapsed}
          onClick={() => onToggleSectionCollapse(section.id)}
        >
          <IconChevron size={14} className="rail-collapse-chevron" />
          {section.emoji ? (
            <span className="rail-group-emoji" aria-hidden>
              {section.emoji}
            </span>
          ) : null}
          <span className="rail-section-label-text">{section.label}</span>
        </button>
        <div
          className={`rail-section-items${section.collapsed ? ' is-collapsed' : ''}`}
          hidden={section.collapsed}
        >
          {body}
        </div>
      </div>
    );
  };

  return (
    <nav
      ref={navRef}
      className={`app-rail ${expanded ? 'expanded' : ''}${mobileDrawer ? ' app-rail-drawer' : ''}`}
      data-testid="app-shell-rail"
      data-nav-mode={hasCustomLayout ? 'custom' : 'default'}
      aria-label="Primary navigation"
      // Drawer semantics (mobile only). aria-modal is asserted only while open;
      // when closed the off-canvas rail is hidden from AT and the tab order.
      {...(mobileDrawer
        ? {
            role: 'dialog',
            'aria-modal': drawerOpen ? 'true' : undefined,
            'aria-hidden': drawerOpen ? undefined : 'true',
            onKeyDown: handleDrawerKeyDown,
          }
        : {})}
    >
      <div className="rail-brand">
        <PrimeBrandMark />
        {mobileDrawer ? (
          <button
            ref={closeButtonRef}
            type="button"
            className="rail-drawer-close"
            onClick={onRequestClose}
            aria-label="Close navigation menu"
            data-testid="mobile-nav-close"
            title="Close menu"
          >
            <IconClose size={18} />
          </button>
        ) : (
          <button
            type="button"
            className="rail-collapse-toggle"
            onClick={onToggle}
            aria-label={expanded ? 'Collapse side navigation' : 'Expand side navigation'}
            aria-expanded={expanded}
            title={expanded ? 'Collapse navigation' : 'Expand navigation'}
          >
            <IconChevron size={14} />
          </button>
        )}
      </div>
      <div className="rail-brand-acronym">
        Program Resource and Intelligence Management Engine
      </div>
      <div className="rail-scroll">
        {hasCustomLayout ? (
          <>
            {customModel.map((section) => renderCustomSection(section))}
            {pinnedSpecials.length ? (
              <div className="rail-section" key="custom:__pinned">
                <div className="rail-section-label">Administration</div>
                {pinnedSpecials.map((item) => renderItem(item.to, item.label, item.icon))}
              </div>
            ) : null}
          </>
        ) : (
          renderedWorkspaces.map((s) => {
            // hasAnyRole mirrors the RequireRoute guard each destination renders
            // behind; the essentials filter can only *remove* items, never
            // bypass RBAC. Empty workspaces vanish via the length check.
            const visible = s.items.filter(
              (i) =>
                rbac.hasAnyRole(roles, i.allow || []) &&
                (navView === 'all' || i.alwaysVisible === true || isEssentialRoute(i.to)),
            );
            if (!visible.length) return null;
            return (
              <div className="rail-section" key={s.label}>
                <div className="rail-section-label">{s.label}</div>
                {visible.map((i) => renderItem(i.to, i.label, i.icon))}
              </div>
            );
          })
        )}
      </div>
      <div className="rail-footer">
        {hasCustomLayout ? null : (
          <button
            type="button"
            className="rail-view-toggle"
            data-testid="nav-view-toggle"
            onClick={() => setNavView(userId, navView === 'all' ? 'essentials' : 'all')}
            aria-pressed={navView === 'all'}
            title={toggleLabel}
          >
            <IconBinoculars size={18} />
            <span className="rail-item-label">{toggleLabel}</span>
          </button>
        )}
        {/* Discoverable deep-link into the personal-menu editor. In default mode
            it sits beneath the essentials toggle; once a custom layout is saved
            it REPLACES the binary toggle as the personalization entry point
            (PRIME-NAV-UX-001, decision 5). */}
        <Link
          to={CUSTOMIZE_MENU_TARGET}
          className="rail-customize-link"
          data-testid="nav-customize-link"
          title="Customize menu"
          onMouseEnter={(e) => showTip(e, 'Customize menu')}
          onMouseLeave={hideTip}
          onFocus={(e) => showTip(e, 'Customize menu')}
          onBlur={hideTip}
        >
          <IconSparkles size={18} />
          <span className="rail-item-label">Customize menu</span>
        </Link>
        {expanded ? (
          <div className="rail-build-meta">
            <div className="rail-build-version mono" data-testid="rail-build-version">
              Build {buildVersionLabel}
            </div>
            {buildTimeLabel ? (
              <div
                className="rail-build-time"
                data-testid="rail-build-time"
                title={buildInfo.builtAt || undefined}
              >
                {buildTimeLabel}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="rail-affiliation" data-testid="rail-affiliation">
          <span className="rail-affiliation-seal">
            <img src="/brand/dha-seal.svg" alt="Defense Health Agency seal" />
          </span>
          <span className="rail-affiliation-text rail-item-label">Defense Health Agency</span>
        </div>
      </div>
      {tip ? (
        <div className="rail-fly-tooltip" role="tooltip" style={{ top: tip.top, left: tip.left }}>
          {tip.label}
        </div>
      ) : null}
    </nav>
  );
}
